import { create } from 'zustand';

export type RightPanel = 'members' | 'pins' | 'search' | 'thread' | null;

/** Screen rectangle of whatever was clicked to open a profile; the card pops out beside it. */
export interface ProfileAnchor {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export type DialogState =
  | { kind: 'createSpace' }
  | { kind: 'joinSpace' }
  | { kind: 'createChannel'; spaceId: string; parentId: string | null; type?: 'text' | 'voice' | 'announcement' | 'forum' | 'media' | 'stage' | 'category' }
  | { kind: 'invite'; spaceId: string; channelId: string }
  | { kind: 'channelSettings'; channelId: string }
  | { kind: 'spaceSettings'; spaceId: string; section?: string }
  | { kind: 'userSettings'; section?: string }
  | { kind: 'profile'; userId: string; spaceId: string | null; anchor?: ProfileAnchor }
  | { kind: 'report'; messageId?: string; userId?: string; spaceId?: string | null }
  | { kind: 'moderate'; action: 'kick' | 'ban' | 'timeout'; spaceId: string; userId: string }
  | { kind: 'newGroup' }
  | { kind: 'quickSwitcher' }
  | { kind: 'shortcuts' }
  | { kind: 'createThread'; channelId: string; messageId: string; defaultName: string }
  | { kind: 'lightbox'; url: string; filename: string }
  | {
      kind: 'confirm';
      title: string;
      body: string;
      confirmLabel: string;
      danger?: boolean;
      onConfirm: () => Promise<void> | void;
    };

export interface Toast {
  id: number;
  kind: 'info' | 'error' | 'success';
  text: string;
}

export interface UiState {
  rightPanel: RightPanel;
  threadId: string | null;
  searchQuery: string;
  /** Mobile: which pane is in front. */
  mobilePane: 'nav' | 'main' | 'side';
  dialog: DialogState | null;
  toasts: Toast[];
  /** A message to scroll to and highlight once its channel loads. */
  jumpTo: { channelId: string; messageId: string } | null;
  replyTo: Record<string, string | undefined>;
  editing: string | null;
}

export const useUi = create<UiState>(() => ({
  // The member list only fits beside the chat on wide screens; elsewhere it opens on demand.
  rightPanel: typeof window !== 'undefined' && window.innerWidth >= 1180 ? 'members' : null,
  threadId: null,
  searchQuery: '',
  mobilePane: 'main',
  dialog: null,
  toasts: [],
  jumpTo: null,
  replyTo: {},
  editing: null,
}));

let toastId = 0;

export const ui = {
  open(dialog: DialogState) {
    useUi.setState({ dialog });
  },
  close() {
    useUi.setState({ dialog: null });
  },
  /** Opens a profile card next to `from` (the clicked avatar or name), or centred without it. */
  openProfile(userId: string, spaceId: string | null, from?: EventTarget | null) {
    let anchor: ProfileAnchor | undefined;
    if (from instanceof Element) {
      const r = from.getBoundingClientRect();
      anchor = { top: r.top, bottom: r.bottom, left: r.left, right: r.right };
    }
    useUi.setState({ dialog: { kind: 'profile', userId, spaceId, anchor } });
  },
  toggleRightPanel(panel: Exclude<RightPanel, null>) {
    useUi.setState((s) => ({ rightPanel: s.rightPanel === panel ? null : panel, mobilePane: s.rightPanel === panel ? 'main' : 'side' }));
  },
  setRightPanel(panel: RightPanel) {
    useUi.setState({ rightPanel: panel, mobilePane: panel ? 'side' : 'main' });
  },
  openThread(threadId: string) {
    useUi.setState({ rightPanel: 'thread', threadId, mobilePane: 'side' });
  },
  search(query: string) {
    useUi.setState({ rightPanel: 'search', searchQuery: query, mobilePane: 'side' });
  },
  setMobilePane(mobilePane: UiState['mobilePane']) {
    useUi.setState({ mobilePane });
  },
  toast(text: string, kind: Toast['kind'] = 'info') {
    const id = ++toastId;
    useUi.setState((s) => ({ toasts: [...s.toasts.slice(-3), { id, kind, text }] }));
    setTimeout(() => useUi.setState((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })), kind === 'error' ? 6000 : 3500);
  },
  dismissToast(id: number) {
    useUi.setState((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
  },
  jumpTo(channelId: string, messageId: string) {
    useUi.setState({ jumpTo: { channelId, messageId } });
  },
  clearJump() {
    useUi.setState({ jumpTo: null });
  },
  setReply(channelId: string, messageId: string | undefined) {
    useUi.setState((s) => ({ replyTo: { ...s.replyTo, [channelId]: messageId } }));
  },
  setEditing(messageId: string | null) {
    useUi.setState({ editing: messageId });
  },
  confirm(opts: Omit<Extract<DialogState, { kind: 'confirm' }>, 'kind'>) {
    useUi.setState({ dialog: { kind: 'confirm', ...opts } });
  },
};
